import { GameState } from './GameState';
import { GameData } from '../data/GameData';
import { GameConfig } from '../core/GameConfig';
import { fanSystem } from './FanSystem';
import { isDramaAired, promoWindowOf } from './ScriptSystem';
import { NormalNpc } from '../data/types';
import { npcPersonalityKey, softLedgerHeatGain } from './GuestCameoSystem';
import { FRIEND_PROMO_ME, FRIEND_PROMO_NPC } from '../data/tables/EncounterTexts';

/**
 * 好友剧宣（2026-10-28 批，现实对标=开播时好友发条宣传微博）：
 * 玩家一番/二番的作品开播后，可请圈内好友站台——消耗好感，抬剧热度（friendPromoted 账本）。
 * 形制=好友客串系统（GuestCameoSystem 方向二）同构：入口同在人脉聊天页选项清单，
 * 「客串是来演戏（大头），宣传是发条微博（小头）」⇒ 消耗与系数取客串一半、与客串**共用**封顶池。
 * ⛔ 好友不沾光涨名气（用户拍板，区别于客串）；⛔ 无拒绝无反噬（好感≥60 已是朋友，确定性兑现——
 * 「被扒」的不确定性留给经纪人商务通稿，私交≠商务）；⛔ 小助理不播报（常驻入口，同客串）。
 */

/** 聊天页「邀宣传」二级列表项（形制同 GuestContractRow） */
export interface PromoContractRow {
    contractId: string;
    scriptId: string;
    scriptName: string;
    /** null=可请；非 null=该行置灰原因（⛔ 成交，点击弹提示） */
    disabledReason: string | null;
}

/** 结算结果（供 UI 拼气泡与结果行；⛔ 无 fame 字段——好友不沾光） */
export interface PromoSettleResult {
    scriptName: string;
    favorCost: number;
    heatGain: number;
    meText: string;
    npcText: string;
}

function pick(arr: string[]): string {
    return arr[Math.floor(Math.random() * arr.length)];
}

export class FriendPromoSystem {
    private static instance: FriendPromoSystem;

    private constructor() { }

    static getInstance(): FriendPromoSystem {
        if (!this.instance) {
            this.instance = new FriendPromoSystem();
        }
        return this.instance;
    }

    /** 请托门槛（null=可请）：与客串同用 GUEST_MIN_FAVOR（好友档）；特殊 NPC 无此语义（调用方不挂行） */
    gate(npcId: string): string | null {
        const state = GameState.getInstance();
        if (!state.player.normalNpcs.some(n => n.id === npcId) || GameData.NPC_MAP[npcId]) return null;
        const favor = state.getFavor(npcId);
        if (favor < GameConfig.GUEST_MIN_FAVOR)
            return `好感度不足：需 ≥${GameConfig.GUEST_MIN_FAVOR}，当前 ${favor}`;
        return null;
    }

    /** 一次站台的固定好感消耗（客串阶梯减半，供入口提示与结算同源） */
    favorCost(npc: NormalNpc): number {
        return GameConfig.FRIEND_PROMO_FAVOR_COSTS[Math.max(1, Math.min(npc.rank, 10)) - 1];
    }

    /** 二级列表全部行：已开播 ∧ 宣传窗口内 ∧ 自己一番/二番（结构性合格的戏一律列出，⛔ 因热度软顶锁行——B+C 案 2026-10-28）；
     *  该好友已在此剧站台过 → 保留该行但挂 disabledReason（置灰可点弹原因，同客串形制）。
     *  窗口口径与自宣（FanSystem.dramaAired）同源走 promoWindowOf；宣传微博无档期冲突，⛔ 查 activeJobs。 */
    contractRows(npcId: string): PromoContractRow[] {
        const state = GameState.getInstance();
        const p = state.player;
        const now = p.year * 12 + p.month;
        const rows: PromoContractRow[] = [];
        for (const c of p.contracts) {
            const script = GameData.SCRIPT_MAP[c.scriptId];
            if (!script) continue;
            if (!isDramaAired(c, script, now)) continue;
            const win = promoWindowOf(c, script, now);
            if (win && (now < win.start || now >= win.start + win.months)) continue;
            const billing = script.roles[c.roleIndex]?.billing;
            if (billing !== 1 && billing !== 2) continue;   // 只有一、二番的作品开得了这个口
            let disabledReason: string | null = null;
            if ((p.promoAskedBy?.[script.id] ?? []).includes(npcId)) {
                disabledReason = `TA 已为《${script.name}》发布过宣传，同一作品仅可请求一次`;
            }
            rows.push({ contractId: c.id, scriptId: script.id, scriptName: script.name, disabledReason });
        }
        return rows;
    }

    /** 可请单 = contractRows 去掉置灰行（结算侧硬校验同源） */
    eligibleContracts(npcId: string): PromoContractRow[] {
        return this.contractRows(npcId).filter(r => !r.disabledReason);
    }

    /** 结构性无单说明（一戏不剩时兜底；各档各说各的因） */
    emptyReason(): string {
        const state = GameState.getInstance();
        const p = state.player;
        const now = p.year * 12 + p.month;
        const aired = p.contracts.filter(c => {
            const script = GameData.SCRIPT_MAP[c.scriptId];
            if (!script || !isDramaAired(c, script, now)) return false;
            const win = promoWindowOf(c, script, now);
            return !win || (now >= win.start && now < win.start + win.months);
        });
        if (aired.length === 0) return '当前没有正在播出的作品';
        const lead = aired.filter(c => {
            const billing = GameData.SCRIPT_MAP[c.scriptId].roles[c.roleIndex]?.billing;
            return billing === 1 || billing === 2;
        });
        if (lead.length === 0) return '在播作品中没有你的一番/二番角色，不满足邀请条件';
        return '当前没有可请求的作品';   // 理论不可达（热度软顶不锁行），兜底防 undefined
    }

    /** 原子结算：扣好感 → 抬剧宣账本（与客串共池、软顶衰减，触顶收益 0 但请求仍成立）→ 拼台词；
     *  ⛔ 动好友 fame；余额不足 ⛔ 成行 */
    settle(npcId: string, contractId: string): PromoSettleResult | null {
        const state = GameState.getInstance();
        const npc = state.player.normalNpcs.find(n => n.id === npcId);
        const row = this.eligibleContracts(npcId).find(r => r.contractId === contractId);
        if (!npc || !row) return null;
        const script = GameData.SCRIPT_MAP[row.scriptId];
        const favorCost = this.favorCost(npc);
        if (state.getFavor(npcId) < favorCost) return null;

        const workHeat = fanSystem.workHeatOf(script);
        const ledgerSum = (state.player.guestPromoted?.[script.id] ?? 0)
            + (state.player.friendPromoted?.[script.id] ?? 0);
        const raw = npc.fame * GameConfig.FRIEND_PROMO_HEAT_K
            * Math.min(1, GameConfig.GUEST_DECAY_ANCHOR / Math.max(1, workHeat));
        const heatGain = softLedgerHeatGain(script, ledgerSum, raw);

        state.addFavor(npcId, -favorCost);
        state.player.friendPromoted = {
            ...(state.player.friendPromoted ?? {}),
            [script.id]: (state.player.friendPromoted?.[script.id] ?? 0) + heatGain,
        };
        // 记「该好友已在此剧站台过」：同一部剧每位好友只请一次（口径同客串一剧一签）
        state.player.promoAskedBy = {
            ...(state.player.promoAskedBy ?? {}),
            [script.id]: [...(state.player.promoAskedBy?.[script.id] ?? []), npcId],
        };

        const meText = pick(FRIEND_PROMO_ME).replace(/\{script\}/g, script.name);
        const npcText = pick(FRIEND_PROMO_NPC[npcPersonalityKey(npc)]).replace(/\{script\}/g, script.name);
        return { scriptName: script.name, favorCost, heatGain, meText, npcText };
    }
}

/** 导出单例 */
export const friendPromoSystem = FriendPromoSystem.getInstance();
